import React, {
  memo,
  useState,
  useEffect,
  ChangeEvent,
  FocusEvent,
  HTMLProps,
  RefObject,
  useCallback,
  useRef,
} from 'react'
import classNames from 'classnames'
import { IconButton } from 'transfix-ui/components/IconButton'
import PatternFormat from 'transfix-ui/components/PatternFormat'
import { createStyles, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import uuid from 'uuid/v4'
import { ClearIcon } from 'transfix-icons'
import 'scss/5_components/_text-input.scss'

type TPhoneInputClassKey =
  | 'root'
  | 'container'
  | 'clearButton'
  | 'clearButtonHidden'
  | 'buttonWrapper'

const BORDER_HEIGHT = 2
const INPUT_HEIGHT_PX = 32
const INPUT_HEIGHT_LARGE_PX = 42

const usePhoneInputStyles = makeStyles(theme =>
  createStyles<TPhoneInputClassKey, { large?: boolean }>({
    root: {
      '&.text-input:focus-within .text-input-container': {
        borderRadius: theme.shape.borderRadius,
        boxShadow: `0 0 0 3px ${brandColors.skyBlue2} !important`,
      },

      '&.text-input.invalid:focus-within .text-input-container': {
        boxShadow: `0 0 0 3px ${brandColors.error0} !important`,
      },
      /**
       * Overrides global styles for `.text-input` that hides box-shadow
       */
      '&.text-input': {
        overflow: 'visible',
      },
    },
    container: {
      '.text-input &.text-input-container': {
        padding: 0,
        height: ({ large }) => (large ? INPUT_HEIGHT_LARGE_PX : INPUT_HEIGHT_PX),
        minWidth: 148,

        '& input[type="tel"]': {
          height: ({ large }) =>
            large ? INPUT_HEIGHT_LARGE_PX - BORDER_HEIGHT : INPUT_HEIGHT_PX - BORDER_HEIGHT,
          lineHeight: 1.375,
          paddingTop: ({ large }) => (large ? theme.spacing(1.5) : theme.spacing(0.5)),
          paddingBottom: ({ large }) => (large ? theme.spacing(1.5) : theme.spacing(0.5)),
          paddingRight: ({ large }) => (large ? theme.spacing(1.5) : theme.spacing(1)),
          paddingLeft: ({ large }) => (large ? theme.spacing(1.5) : theme.spacing(1)),

          '&:focus': {
            border: '1px solid',
            borderColor: `${brandColors.skyBlue6} !important`,
            zIndex: 2,
          },
        },
      },
    },
    clearButton: {
      position: 'relative',
      left: -4,
      padding: theme.spacing(1),
      minWidth: 'auto',
      transition: `opacity 0.15s ${theme.transitions.easing.easeInOut}`,
      opacity: 1,
    },
    clearButtonHidden: {
      transition: `opacity 0.15s ${theme.transitions.easing.easeInOut}`,
      opacity: 0,
    },
    buttonWrapper: {
      '.text-input .text-input-container &': {
        display: 'inline-block',
        marginRight: theme.spacing(0.5),
      },
    },
  })
)

// TODO: Combine this with TextInput field since both are reusing mostly the same code
export interface IPhoneInputProps extends HTMLProps<HTMLInputElement> {
  label?: string
  value?: string | number
  className?: string
  invalid?: boolean
  validationMessage?: string
  large?: boolean
  format?: string
  mask?: string
  disabled?: boolean
  clearable?: boolean
  required?: boolean
  dataTest?: string
  /**
   * @default true
   */
  roundedCorner?: boolean
  onClearAll?: (val: string) => void
  /**
   * This field will give you the possibility to reference the container of the phone input, i.e., in case that you need to focus on that element
   */
  containerRef?: RefObject<HTMLDivElement>
}

export const PhoneInput = memo<IPhoneInputProps>(
  ({
    className: classNameProp = '',
    clearable = true,
    containerRef,
    dataTest,
    disabled,
    format = '(###) ###-####',
    id: idProp,
    invalid,
    label,
    large,
    mask = '_',
    name,
    onBlur,
    onChange,
    onClearAll: onClearAllProp,
    placeholder = '(555) 555-5555',
    required,
    roundedCorner = true,
    validationMessage,
    value: valueProp,
  }) => {
    const id = useRef(idProp || uuid())
    // TODO: refactor so it reuses TextInput logic
    const [phoneValue, setPhoneValue] = useState(valueProp)

    /**
     * Keep local state updated with the value of the input
     */
    useEffect(() => {
      setPhoneValue(valueProp)
    }, [valueProp])

    const blurHandler = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        e.preventDefault()
        e.stopPropagation()
        const { value: eventValue } = e.target

        if (onBlur && eventValue) {
          onBlur(e)
        }
      },
      [onBlur]
    )

    const onChangeHandler = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault()
        e.stopPropagation()
        const { value: eventValue } = e.target
        setPhoneValue(eventValue)
        if (onChange) {
          onChange(e)
        }
      },
      [onChange]
    )

    const onClearAll = useCallback(() => {
      setPhoneValue('')

      if (onClearAllProp) {
        onClearAllProp('')
      }
    }, [onClearAllProp])

    const validationText = validationMessage

    const classes = usePhoneInputStyles({ large })

    const className = classNames('text-input', classes.root, classNameProp, {
      invalid,
      disabled,
      'rounded-corner': roundedCorner,
    })

    const containerClassName = classNames(classes.container, 'text-input-container')
    const iconButtonClassName = classNames(classes.clearButton, 'clear-all-button', {
      [classes.clearButtonHidden]: !phoneValue,
    })

    return (
      <div className={className} ref={containerRef} data-test='PhoneInput-root'>
        {label && (
          <label
            htmlFor={id.current}
            className={`label ${disabled ? 'disabled' : ''} ${required ? 'required' : ''}`}>
            {label}
          </label>
        )}
        <div className={containerClassName}>
          <PatternFormat
            className={invalid ? 'invalid' : ''}
            type='tel'
            displayType='input'
            value={phoneValue}
            id={id.current}
            onBlur={blurHandler}
            onChange={onChangeHandler}
            disabled={disabled}
            placeholder={placeholder}
            format={format}
            mask={mask}
            data-test={dataTest}
            name={name}
          />
          {clearable && (
            <div className={classes.buttonWrapper}>
              <IconButton
                data-test={`${dataTest}-clear-all-button`}
                Icon={ClearIcon}
                color='tertiary'
                onClick={onClearAll}
                label={`Clear ${label} field`}
                className={iconButtonClassName}
                size='large'
              />
            </div>
          )}
        </div>
        {validationText && <span className='validation-message'>{validationText}</span>}
      </div>
    )
  }
)

export default PhoneInput
